/**
Copyright (C) 2018 Andras Sarvari <andras.sarvari@sarvaria.net>

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted, provided that the above
copyright notice and this permission notice appear in all copies.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
*/

/* jshint browser: true, esversion: 6, node:true */
/* globals console */

import React from 'react';
import PropTypes from 'prop-types';
import Control from '../Control/Control';
import Label from '../Label/Label';
import $ from '../Util/Util';
import './Textbox.css';

/**
 * Represents the textbox component.
 */

export default class Textbox extends Control {

    /** Label positions. */
    static get LABELPOSITION() { return {
        TOP:   'top',
        LEFT:  'left'
    };}

    /**
     * Properties.
     */

    static propTypes = {
        label:            PropTypes.string,
        labelwidth:       PropTypes.number,
        labelposition:    PropTypes.string,
        maxlength:        PropTypes.number,
        password:         PropTypes.bool,
        disabled:         PropTypes.bool,
        touppercase:      PropTypes.bool,
        tocapitalize:     PropTypes.bool,
        isrequired:       PropTypes.bool,
        validateontyping: PropTypes.bool,
        validateonblur:   PropTypes.bool,
        requiredmessage:  PropTypes.string,
        placeholder:      PropTypes.string,
        tooltip:          PropTypes.string,
        width:            PropTypes.number,
        comment:          PropTypes.string,
        onblur:           PropTypes.func,
        onchange:         PropTypes.func
    }

    /**
     * Default state.
     */

    state = { 
        error:      null, 
        showerror:  false
    }

    /**
     * Gets the error message for the manadatory fields.
     * 
     * @return  {String}    message     required message
     */

    getRequiredMessage = () => {
        return this.props.requiredmessage || 'Mandatory field';
    }
    
    /**
     * Sets the focus to the textbox.
     */
    
    setFocus = () => {
        this.input.focus();
    }

    /**
     * Gets the value of the textbox.
     *
     * @returns {String}  value   value of the textbox
     */

    getValue = () => {
      
        // Getting the value of the textbox

        if (this.props.touppercase) {
            return this.input.value.toUpperCase();
        } else if (this.props.tocapitalize) {

            // Capitalizing each words
            
            let value = this.input.value.split(' ');
            let text = '';

            value.forEach((item, index) => {
                text += ((index > 0 ? ' ' : '') + $.capitalizeFirstLetter(item));
            });

            return text;
        } else {
            return this.input.value;
        }
    }

    /**
     * Sets the value of the textbox.
     *
     * @param {String}  value           value of the textbox
     * @param {Boolean} novalidation    no validate during the value set
     */

    setValue = (value, novalidation) => {
      
        // Setting the value of the textbox

        this.input.value = value;

        if (!novalidation) {
            this.validate();
        }
    }

    /**
     * Clears the value and the error message of the textbox.
     */

    clearValue = () => {
      
        // Clearing the value

        this.input.value = '';
        this.clearError();
    }

    /**
     * Clears the error message of the textbox.
     */

    clearError = () => {
      
        // Clearing the error

        this.setError();
    }

    /**
     * Sets the error message of the textbox.
     *
     * @param {String}  message   error message that has to show
     */

    setError = (message) => {

        // Does the error message specified?

        if (message) {

            // Showing the error message

            this.setState({ error: message }, () => {
                setTimeout(() => {
                    this.setState({ showerror: true });
                }, 20);
            });
        } else {

            // Clearing the error message

            this.setState({ showerror: false }, () => {
                setTimeout(() => {
                    this.setState({ error: null });
                }, 250);
            });
        }
    }

    /**
     * Checks that the value of the textbox is empty or not.
     *
     * @param     {String}  value   value that has to check (optional)
     * @returns   {Boolean} empty   specified value or the value of the textbox is
     *                              empty or not
     */

    isEmpty = (value) => {
        
        // Getting the value

        let v = (value || this.getValue());

        // Checking the value

        return (v === null || v === undefined) ? true : v.toString().trim() === '';
    }

    /**
     * Validates the textbox.
     * 
     * @param     {String}  value   value that has to check (optional)
     * @returns   {Boolean} valid   specified value or the value of the textbox is
     *                              valid or not
     */

    validate = (value) => {

        // Does the value of the textbox valid?

        if (!(this.props.isrequired && this.isEmpty(value))) {
            if (!value) {
                this.clearError();
            }

            return true;
        }

        // Textbox is invalid

        this.setError(this.getRequiredMessage());
        return false;
    }

    /**
     * Validates the textbox on typing.
     */

    _validateOnTyping = () => {

        // Validating

        if (this.props.validateontyping) {
            this.validate();
        }

        // Firing the "onchange" event

        if (this.props.onchange) {
            this.props.onchange();
        }
    }

    /**
     * Validates the textbox on blur.
     */

    _validateOnBlur = () => {

        // Validating
        
        if (this.props.validateonblur) {
            this.validate();
        }

        // Firing the "onblur" event

        if (this.props.onblur) {
            this.props.onblur();
        }
    }

    /**
     * Fires the "onEnter" event if the user presses the ENTER key.
     */

    _isEnter = (event) => {
        if ((event.keyCode ? event.keyCode : event.which) === 13 && this.props.onEnter) {
            this.props.onEnter();
        }
    }

    /**
     * Gets the style of the textbox.
     * 
     * @return  {Object}    style   style of the textbox
     */

    getStyle = () => {

        // Preparing

        let style = this._getStyle();
        
        // Adding style attributes
        
        style.marginBottom = (this.props.bottomgap || 0) + 16 + 'px';

        // Returns the style

        return style;
    }

    /**
     * Textbox renderer.
     */

    render() {

        // Setting default label position

        let labelposition = this.props.labelposition || Textbox.LABELPOSITION.LEFT;

        // Rendering

        return (

            /* Textbox container */

            <div className="textbox" style={this.getStyle()} title={this.props.tooltip}>
                <div className={`${this.props.label && labelposition === Textbox.LABELPOSITION.LEFT ? 'horizontal' : 'vertical'}`}>

                    {/* Label */}
                
                    { this.props.label && <div><Label text={this.props.label} topgap={labelposition === Textbox.LABELPOSITION.TOP ? 0 : 6} rightgap={this.props.labelwidth || labelposition === Textbox.LABELPOSITION.TOP ? 0 : 8} width={this.props.labelwidth} isrequired={this.props.isrequired} requiredmessage={this.getRequiredMessage()} /></div>}
                    
                    <div>

                        {/* Inputbox */}

                        <input 
                            ref={c => {this.input = c;}} 
                            type={this.props.password ? 'password' : 'text'}
                            disabled={this.props.disabled}
                            placeholder={this.props.placeholder}
                            className={`textbox-input ${this.state.error ? 'textbox-input-invalid' : ''} textbox-input-${this.props.disabled ? 'disabled' : 'enabled'}`}
                            maxLength={this.props.maxlength || 1000}
                            style={{ width: (this.props.width || 200) + 'px', textTransform: this.props.touppercase ? 'uppercase' : this.props.tocapitalize ? 'capitalize' : 'none' }}
                            onFocus={(event) => {event.target.select();}}
                            onKeyDown={this._isEnter}
                            onKeyUp={this._validateOnTyping}
                            onBlur={this._validateOnBlur}
                        />

                        {/* Comment */}

                        { this.props.comment && <div className="textbox-comment">{this.props.comment}</div> }

                        {/* Error message */}

                        {this.state.error && (<div className={`textbox-error ${this.state.showerror ? 'textbox-error-show' : ''}`} style={{ top: (this.props.comment ? -4 : 8) + 'px'}}>
                            <div className="textbox-error-container">
                                <div className="textbox-error-content">{this.state.error}</div>
                            </div>
                        </div>)}
                    </div>
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/Textbox/Textbox.js